import { ActivatedRoute } from '@angular/router';
import { identity, map, pickBy, range } from 'lodash';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { catchError, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';

import { BlogType, ContentJsonType, LandingPageTypeEnum, LandingPagesWithoutTranslationType } from '@sifca-monorepo/ecommerce-generator';
import { AppService, LandingPagesService, SeoService, SharedService } from '../../services';

@Component({
  template: '',
})
export class BaseBlogListComponent implements OnInit {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  sideLimit = 4;
  totalCount = 0;
  loaded = false;
  currentPage = 1;
  blogLimit: number;
  blogs: BlogType[];
  numberOfPages = 3;
  startingPages = 0;
  links: any[] = [];
  posId: string = '';
  pageRange: number[];
  totalOfPages: number;
  seo: ContentJsonType;
  sideBlogLoaded = false;
  sideBlogs: BlogType[] = [];
  landingPages: LandingPagesWithoutTranslationType[];

  constructor(
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected appService: AppService,
    protected activeRoute: ActivatedRoute,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected landingPagesService: LandingPagesService,
    @Inject(PLATFORM_ID) protected platformId: Object,
    @Inject(DOCUMENT) protected document: Document,
  ) {
    this.sharedService.posId$.subscribe((posId) => {
      this.posId = posId;
    });
    this.landingPagesService
      .findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.BLOG])
      .pipe(
        catchError(() => {
          return of(null);
        }),
        switchMap((res) => {
          this.landingPages = res.objects;
          return this.seoService.getPagesSEOJSON(this.landingPages[0].id);
        }),
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
        this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
          const link = this.renderer.createElement('link');
          if (seoLink.sizes) {
            link.sizes = seoLink.sizes;
          }
          if (seoLink.href) {
            link.href = seoLink.href;
          }
          if (seoLink.rel) {
            link.rel = seoLink.rel;
          }
          this.renderer.appendChild(this.document.head, link);
          return link;
        });
      });
  }

  ngOnInit(): void {
    this.activeRoute.queryParams.pipe(takeUntil(this.unsubscribeAll)).subscribe((params) => {
      if (params['page']) {
        this.appService.productsPageIndex$ = Number(params['page'] - 1);
      } else {
        this.appService.productsPageIndex$ = 0;
      }
      this.appService
        .findBlogsByTargetPaginated(this.posId)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe();
    });
    this.blogLimit = this.appService.blogLimit;
    this.appService.blogs$.pipe(takeUntil(this.unsubscribeAll)).subscribe((blogs) => {
      if (blogs) {
        this.blogs = blogs.objects;
        this.totalOfPages = blogs.count / this.blogLimit;
        this.loaded = true;
        this.totalCount = blogs.count;
        if (this.numberOfPages < this.totalOfPages) {
          this.pageRange = range(this.startingPages, this.numberOfPages);
        } else {
          this.pageRange = range(this.startingPages, this.totalOfPages);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
    this.appService
      .getSideBlogs(this.posId, 0, this.sideLimit)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.sideBlogs = res;
          this.sideBlogLoaded = true;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  getLastPage() {
    this.document.defaultView?.scrollTo(0, 0);
    this.appService.productsPageIndex$ = this.totalOfPages - 1;
    this.pageRange = range(this.totalOfPages - 1, this.totalOfPages);
    this.currentPage = this.totalOfPages;
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  getFirstPage() {
    this.document.defaultView?.scrollTo(0, 0);
    this.currentPage = 1;
    this.appService.productsPageIndex$ = 0;
    this.pageRange = range(0, 2);
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  onPageChanged(newPage: number) {
    this.appService.productsPageIndex$ = newPage - 1;
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
